import type { PayloadAction } from '@reduxjs/toolkit'
import { createSlice } from '@reduxjs/toolkit'

export interface CrownInput {
  backgroundcolor: string | number | null[]
  fullmonolithiceffect: string
  protocol: string
  stainingbrand: string
  x?: number
  y?: number
  crownPlacement?: string
}

export const GINGIVA_STAINING_REGIONS = {
  region1: 'Region 1',
  region2: 'Region 2',
  region3: 'Region 3',
}

export interface gingivaState {
  gingivastainingbrand: string
  selectedGingivaStainingKey: string
  gingivaStaing: {
    [imageId: string]: {
      [key: string]: {
        targetcolor?: (number | null | string)[]
        position?: { x: number; y: number }
        output?: any
      }
    }
  }

  gingivaRecipeOutput: any
  gingivaSliderValues: {
    [region: string]: number
  }
  gingivaCeramicBrand: string
  zirkoniaColor: string
  selectedGingivakey: string
  gingivaCeramic: any
  gingivaCeramicOutput: any
  gingivaTarget: any
  gingivaCeramicTarget: any
}

const initialGingivaSliderValues: { [region: string]: number } = Object.keys(
  GINGIVA_STAINING_REGIONS,
).reduce(
  (acc, key) => {
    acc[key] = 0.5
    return acc
  },
  {} as { [region: string]: number },
)

const initialState: gingivaState = {
  selectedGingivaStainingKey: '',
  gingivastainingbrand: '',
  gingivaStaing: {},
  gingivaRecipeOutput: {},
  gingivaTarget: {},

  //Gingiva Ceramic
  gingivaSliderValues: initialGingivaSliderValues,
  gingivaCeramicBrand: '',
  zirkoniaColor: '',
  selectedGingivakey: '',
  gingivaCeramic: {},
  gingivaCeramicOutput: {},
  gingivaCeramicTarget: {},
}

export const gingivaSlice = createSlice({
  name: 'gingiva',
  initialState,
  reducers: {
    setGingivaStainingBrand: (state, action: PayloadAction<string>) => {
      state.gingivastainingbrand = action.payload
    },
    setSelectedGingivaStainingKey: (state, action: PayloadAction<string>) => {
      state.selectedGingivaStainingKey = action.payload
    },
    setGingivaStainingTarget: (
      state,
      action: PayloadAction<{
        imageId: string
        targetcolor?: (number | null | string)[]
        position?: { x: number; y: number }
        output?: any
        key?: string
      }>,
    ) => {
      const {
        imageId,
        targetcolor,
        position,
        output,
        key: actionKey,
      } = action.payload
      const key = actionKey || state.selectedGingivaStainingKey
      if (!key || !imageId) return

      if (!state.gingivaStaing) {
        state.gingivaStaing = {}
      }

      if (!state.gingivaStaing[imageId]) {
        state.gingivaStaing[imageId] = {}
      }

      if (!state.gingivaStaing[imageId][key]) {
        state.gingivaStaing[imageId][key] = {}
      }

      const existing = state.gingivaStaing[imageId][key]

      state.gingivaStaing[imageId][key] = {
        ...existing,
        ...(targetcolor && { targetcolor }),
        ...(position && { position }),
        ...(output && {
          output: {
            ...existing.output,
            ...output,
          },
        }),
      }
    },
    clearGingivaStainingState: (state) => {
      return {
        ...state,
        selectedGingivaStainingKey: initialState.selectedGingivaStainingKey,
        gingivastainingbrand: initialState.gingivastainingbrand,
        gingivaStaing: initialState.gingivaStaing,
      }
    },
    setGingivaRecipeOutput: (state, action) => {
      const { imageId, key, result } = action.payload

      if (!state.gingivaRecipeOutput) {
        state.gingivaRecipeOutput = {}
      }

      if (!state.gingivaRecipeOutput[imageId]) {
        state.gingivaRecipeOutput[imageId] = {}
      }

      if (result === undefined) {
        delete state.gingivaRecipeOutput[imageId][key]
      } else {
        state.gingivaRecipeOutput[imageId][key] = {
          ...result,
          generatedAt: Date.now(),
          expiresAt: Date.now() + 24 * 60 * 60 * 1000,
        }
      }
    },
    removeGingivaStainingTarget: (
      state,
      action: PayloadAction<{ imageId: string; key: string }>,
    ) => {
      const { imageId, key } = action.payload

      if (state.gingivaStaing?.[imageId]?.[key]) {
        delete state.gingivaStaing[imageId][key]

        if (Object.keys(state.gingivaStaing[imageId]).length === 0) {
          delete state.gingivaStaing[imageId]
        }
      }

      if (state.gingivaTarget?.[imageId]?.[key]) {
        delete state.gingivaTarget[imageId][key]
        if (Object.keys(state.gingivaTarget[imageId]).length === 0) {
          delete state.gingivaTarget[imageId]
        }
      }
    },

    //Gingiva Ceramic
    updateRangeSlider: (
      state,
      action: PayloadAction<{ name: string; value: number }>,
    ) => {
      const { name, value } = action.payload
      state.gingivaSliderValues[name] = value
    },
    setGingivaCeramicBrand: (state, action: PayloadAction<string>) => {
      state.gingivaCeramicBrand = action.payload
    },
    setZirkoniaColor: (state, action: PayloadAction<string>) => {
      state.zirkoniaColor = action.payload
    },
    setSelectedGingivaKey: (state, action: PayloadAction<string>) => {
      state.selectedGingivakey = action.payload
    },
    setGingivaCeramicTarget: (
      state,
      action: PayloadAction<{
        imageId: string
        targetcolor?: (number | null | string)[]
        position?: { x: number; y: number }
        output?: any
        key?: string
      }>,
    ) => {
      const {
        imageId,
        targetcolor,
        position,
        output,
        key: actionKey,
      } = action.payload
      const key = actionKey || state.selectedGingivakey
      if (!key || !imageId) return

      if (!state.gingivaCeramic) {
        state.gingivaCeramic = {}
      }

      if (!state.gingivaCeramic[imageId]) {
        state.gingivaCeramic[imageId] = {}
      }

      if (!state.gingivaCeramic[imageId][key]) {
        state.gingivaCeramic[imageId][key] = {}
      }

      const existing = state.gingivaCeramic[imageId][key]

      state.gingivaCeramic[imageId][key] = {
        ...existing,
        ...(targetcolor && { targetcolor }),
        ...(position && { position }),
        ...(output && {
          output: {
            ...existing.output,
            ...output,
          },
        }),
      }
    },
    setGingivaCeramicRecipeOutput: (state, action) => {
      const { imageId, key, result } = action.payload

      if (!state.gingivaCeramicOutput) {
        state.gingivaCeramicOutput = {}
      }

      if (!state.gingivaCeramicOutput[imageId]) {
        state.gingivaCeramicOutput[imageId] = {}
      }

      if (result === undefined) {
        delete state.gingivaCeramicOutput[imageId][key]
      } else {
        state.gingivaCeramicOutput[imageId][key] = {
          ...result,
          generatedAt: Date.now(),
          expiresAt: Date.now() + 24 * 60 * 60 * 1000,
        }
      }
    },
    removeGingivaCeramicTarget: (
      state,
      action: PayloadAction<{ imageId: string; key: string }>,
    ) => {
      const { imageId, key } = action.payload

      if (state.gingivaCeramic?.[imageId]?.[key]) {
        delete state.gingivaCeramic[imageId][key]

        if (Object.keys(state.gingivaCeramic[imageId]).length === 0) {
          delete state.gingivaCeramic[imageId]
        }
      }

      if (state.gingivaCeramicTarget?.[imageId]?.[key]) {
        delete state.gingivaCeramicTarget[imageId][key]
        if (Object.keys(state.gingivaCeramicTarget[imageId]).length === 0) {
          delete state.gingivaCeramicTarget[imageId]
        }
      }
    },

    setGingivaTargetColor: (
      state,
      action: PayloadAction<{
        imageId: string
        key: string
        targetcolor: number[]
      }>,
    ) => {
      const { imageId, key, targetcolor } = action.payload
      if (!imageId || !key) return

      if (!state.gingivaTarget[imageId]) {
        state.gingivaTarget[imageId] = {}
      }

      state.gingivaTarget[imageId][key] = targetcolor
    },

    setGingivaCeramicTargetColor: (
      state,
      action: PayloadAction<{
        imageId: string
        key: string
        targetcolor: number[]
      }>,
    ) => {
      const { imageId, key, targetcolor } = action.payload
      if (!imageId || !key) return

      if (!state.gingivaCeramicTarget[imageId]) {
        state.gingivaCeramicTarget[imageId] = {}
      }

      state.gingivaCeramicTarget[imageId][key] = targetcolor
    },
  },
})

export const {
  setSelectedGingivaStainingKey,
  setGingivaStainingTarget,
  setGingivaStainingBrand,
  clearGingivaStainingState,
  setGingivaRecipeOutput,
  removeGingivaStainingTarget,
  setGingivaTargetColor,

  //Gingiva Ceramic
  updateRangeSlider,
  setGingivaCeramicBrand,
  setZirkoniaColor,
  setSelectedGingivaKey,
  setGingivaCeramicTarget,
  setGingivaCeramicRecipeOutput,
  removeGingivaCeramicTarget,
  setGingivaCeramicTargetColor,
} = gingivaSlice.actions

export default gingivaSlice.reducer
